import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

/**
 * Primary navigation. Routes land across M10–M12; entries appear here
 * as their pages ship so navigation never dead-ends. Role-gated items
 * hide below their required rung (the API still enforces).
 */
const NAV_ITEMS: {
  to: string;
  label: string;
  minRole?: 'agent' | 'admin';
}[] = [
  { to: '/', label: 'Overview' },
  { to: '/resources', label: 'Resources' },
  { to: '/alerts', label: 'Alerts' },
  { to: '/collectors', label: 'Collectors' },
  { to: '/discovery', label: 'Discovery', minRole: 'agent' },
  { to: '/dashboards', label: 'Dashboards' },
  { to: '/alerting', label: 'Alerting', minRole: 'agent' },
  { to: '/status-pages', label: 'Status pages', minRole: 'admin' },
  { to: '/migration', label: 'Migration', minRole: 'admin' },
  // Member-reachable: API tokens are self-scoped; privileged tabs
  // gate themselves inside the page.
  { to: '/settings', label: 'Settings' },
];

export function Sidebar({
  id,
  open = false,
  onNavigate,
  onClose,
}: {
  id?: string;
  open?: boolean;
  /** Called when a nav item is chosen (closes the phone-width drawer). */
  onNavigate?: () => void;
  /** Phone-width close button (the open drawer covers the header toggle). */
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const { hasRole } = useTenant();
  return (
    <nav
      id={id}
      className={open ? 'app-sidebar app-sidebar-open' : 'app-sidebar'}
      aria-label="Primary"
    >
      {onClose && (
        <button
          type="button"
          className="btn btn-ghost nav-close"
          aria-label="Close menu"
          onClick={onClose}
        >
          ✕
        </button>
      )}
      <div className="app-brand">Detect</div>
      <ul className="nav-list">
        {NAV_ITEMS.filter((item) => !item.minRole || hasRole(item.minRole)).map(
          (item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-link-active' : 'nav-link'
                }
              >
                {item.label}
              </NavLink>
            </li>
          )
        )}
        {user?.globalRole === 'superadmin' && (
          <li>
            <NavLink
              to="/platform"
              onClick={onNavigate}
              className={({ isActive }) =>
                isActive ? 'nav-link nav-link-active' : 'nav-link'
              }
            >
              Platform
            </NavLink>
          </li>
        )}
      </ul>
    </nav>
  );
}
